import { useState, useEffect, useMemo } from "react";
import {
  buildReflections, availableFactors, CRISIS_ONLY_NOTE, FEEDBACK_OPTIONS, RULE_VERSION,
} from "./signalJournal";
import {
  EMERGENCY_NUMBERS, OTHER_COUNTRY, LOCAL_EMERGENCY_FALLBACK, emergencyFor, loadCountry, saveCountry, telHref,
} from "./emergencyNumbers";

// Personal Signal Journal. Governed by docs/governance/signal-journal-packet.md:
// own entries only, visible evidence, Warrior controls, no scores, no
// predictions, no treatment advice, no community data, and a separate
// always-visible urgent-symptoms path.

const SETTINGS_URL = (base) => `${base}/rest/v1/signal_journal_settings`;
const FEEDBACK_URL = (base) => `${base}/rest/v1/signal_journal_feedback`;
const VERSIONS_URL = (base) => `${base}/rest/v1/signal_journal_plan_versions`;
const FEEDBACK_RPC_URL = (base) => `${base}/rest/v1/rpc/sj_record_feedback`;

// Approved privacy statement (Codex correction 3). It matches what the system
// actually does: the journal is per-account, never used in community views, and
// stored feedback can be reached by authorized administrators for security,
// safety, and support. Do not reword without updating the governance record.
export const JOURNAL_PRIVACY =
  "Your journal is private to your account and is not included in community insights. Authorized administrators may access stored feedback only for security, safety, and support purposes.";
const EMPTY_SETTINGS = { muted_keys: [], excluded_factors: [], plans: {} };
const GENERAL_PLAN = "_general";

// Signs that need care now, from CDC guidance for people with sickle cell
// disease. Pending clinical reviewer sign-off (see PR checklist).
const URGENT_SIGNS = [
  "Fever of 101°F (38.3°C) or higher",
  "Chest pain or trouble breathing",
  "Sudden weakness, numbness, trouble speaking, or confusion",
  "Severe headache or sudden vision changes",
  "Pain that your own plan isn't bringing down",
  "An erection lasting more than 4 hours",
  "Sudden swelling of the belly, or very pale or yellow skin",
];

function headers(base, token) {
  return { apikey: base.anonKey, Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

async function loadSettings(api, token) {
  const res = await fetch(`${SETTINGS_URL(api.url)}?select=*`, { headers: headers(api, token) });
  if (!res.ok) throw new Error(`settings ${res.status}`);
  const rows = await res.json();
  return rows[0] ? { ...EMPTY_SETTINGS, ...rows[0] } : EMPTY_SETTINGS;
}

async function saveSettings(api, token, settings) {
  const res = await fetch(SETTINGS_URL(api.url), {
    method: "POST",
    headers: { ...headers(api, token), Prefer: "resolution=merge-duplicates,return=minimal" },
    body: JSON.stringify({
      muted_keys: settings.muted_keys,
      excluded_factors: settings.excluded_factors,
      plans: settings.plans,
      updated_at: new Date().toISOString(),
    }),
  });
  if (!res.ok) throw new Error(`save ${res.status}`);
}

async function loadFeedback(api, token) {
  const res = await fetch(
    `${FEEDBACK_URL(api.url)}?select=id,reflection_key,feedback,shown_text,created_at&withdrawn_at=is.null&order=created_at.desc`,
    { headers: headers(api, token) });
  if (!res.ok) throw new Error(`feedback ${res.status}`);
  return res.json();
}

// Every saved version of every plan, newest first. Written by a database
// trigger on save, so the client can read the history but never write it.
async function loadPlanVersions(api, token) {
  const res = await fetch(`${VERSIONS_URL(api.url)}?select=id,plan_key,plan_text,created_at&order=created_at.desc,id.desc`,
    { headers: headers(api, token) });
  if (!res.ok) throw new Error(`versions ${res.status}`);
  return res.json();
}

function groupVersions(rows) {
  const by = {};
  rows.forEach((v) => { (by[v.plan_key] = by[v.plan_key] || []).push(v); });
  return by;
}

// Withdraw from the Warrior's view. The row stays in the database (paper trail).
async function withdrawFeedback(api, token, id) {
  const res = await fetch(`${FEEDBACK_URL(api.url)}?id=eq.${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { ...headers(api, token), Prefer: "return=minimal" },
    body: JSON.stringify({ withdrawn_at: new Date().toISOString() }),
  });
  if (!res.ok) throw new Error(`withdraw ${res.status}`);
}

// One server-side transaction (Codex correction 1): the feedback row and, for
// "not_me" / "harmful", the mute are written together or not at all. The user
// is taken from the session token on the server; the client never sends one.
// A retry of the same feedback returns the existing row instead of a duplicate.
export async function recordFeedback(api, token, reflection, feedback) {
  const res = await fetch(FEEDBACK_RPC_URL(api.url), {
    method: "POST",
    headers: headers(api, token),
    body: JSON.stringify({
      p_reflection_key: reflection.key,
      p_feedback: feedback,
      p_rule_version: reflection.ruleVersion,
      p_source_fields: reflection.sourceFields,
      p_evidence_ids: reflection.evidence.map((e) => String(e.id)),
      p_shown_text: reflection.text,
    }),
  });
  if (!res.ok) throw new Error(`feedback ${res.status}`);
  const row = await res.json();
  if (!row || row.id == null) throw new Error("feedback: empty response");
  return row; // { id, reflection_key, feedback, shown_text, created_at, muted_keys }
}

function Btn({ C, active, color, onClick, children, label, disabled }) {
  const c = color || C.teal;
  return (
    <button
      type="button" onClick={onClick} aria-pressed={active} aria-label={label} disabled={disabled}
      style={{
        minHeight: 36, padding: "6px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
        border: `1px solid ${active ? c : C.border}`, background: active ? `${c}22` : "transparent",
        color: active ? c : C.muted,
      }}
    >{children}</button>
  );
}

// Heading and the call button are always visible; the warning-sign list is one
// tap away so the panel stays calm and doesn't push reflections off a phone
// screen. The number follows the country the Warrior picks (Council Decision B);
// with no country picked, it tells them to contact their local emergency service.
const COUNTRY_OPTIONS = Object.entries(EMERGENCY_NUMBERS)
  .map(([code, c]) => ({ code, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function UrgentPanel({ C }) {
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(() => loadCountry());
  const em = emergencyFor(country);
  const choose = (code) => { setCountry(code); saveCountry(code); };
  const callText = em.number ? `call ${em.number}` : "contact your local emergency service";
  return (
    <section aria-labelledby="sj-urgent" style={{
      border: `1px solid ${C.red}55`, background: `${C.red}0d`, borderRadius: 12, padding: "12px 16px", marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div id="sj-urgent" style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 13, color: C.red }}>
          Urgent symptoms? This is a separate path.
        </div>
        {em.number ? (
          <a href={telHref(em.number)} style={{
            display: "inline-block", minHeight: 36, lineHeight: "36px", padding: "0 16px", borderRadius: 8,
            background: C.red, color: "#fff", fontWeight: 600, fontSize: 13, textDecoration: "none",
          }}>{em.label}</a>
        ) : (
          <span style={{ fontWeight: 600, fontSize: 13, color: C.red }}>{LOCAL_EMERGENCY_FALLBACK}</span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
        <label htmlFor="sj-country">Emergency number for</label>
        <select id="sj-country" value={em.code} onChange={(e) => choose(e.target.value)}
          style={{ minHeight: 32, borderRadius: 6, padding: "2px 6px", background: "transparent", color: "inherit", border: `1px solid ${C.red}55` }}>
          {COUNTRY_OPTIONS.map((o) => <option key={o.code} value={o.code} style={{ color: "#111" }}>{o.name}</option>)}
          <option value={OTHER_COUNTRY} style={{ color: "#111" }}>Another country</option>
        </select>
        {em.alt && <span>Also: {em.alt}</span>}
      </div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="sj-urgent-signs"
        style={{ background: "none", border: "none", padding: "8px 0 0", color: C.red, fontSize: 12, cursor: "pointer" }}>
        {open ? "Hide warning signs" : "See warning signs"}
      </button>
      {open && (
        <div id="sj-urgent-signs">
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.6, margin: "6px 0" }}>
            Your journal is for noticing patterns. It is not for emergencies. If you have any of these, {callText} or go to the nearest emergency room now:
          </div>
          <ul style={{ margin: "0 0 4px 18px", padding: 0, fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.7 }}>
            {URGENT_SIGNS.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

// Earlier versions of one plan (the newest version is the current plan).
function PlanVersions({ C, versions, onUse }) {
  const [open, setOpen] = useState(false);
  const earlier = (versions || []).slice(1);
  if (!earlier.length) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ background: "none", border: "none", padding: "4px 0", color: C.muted, fontSize: 11, cursor: "pointer" }}>
        Earlier versions ({earlier.length}) · {open ? "hide" : "show"}
      </button>
      {open && earlier.map((v) => (
        <div key={v.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "4px 0", fontSize: 12, color: C.muted }}>
          <span><span style={{ color: C.text }}>{(v.created_at || "").slice(0, 10)}</span> · {v.plan_text || "(empty)"}</span>
          <Btn C={C} onClick={() => onUse(v.plan_text)}>Use this</Btn>
        </div>
      ))}
    </div>
  );
}

const FEEDBACK_LABEL = Object.fromEntries(FEEDBACK_OPTIONS.map((o) => [o.id, o.label]));

// The Warrior's feedback history. "Remove" withdraws an item from their view;
// the record itself is kept (paper trail) and they are told so.
function FeedbackHistory({ C, items, onWithdraw }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ background: "none", border: "none", padding: "4px 0", color: C.teal, fontSize: 12, cursor: "pointer" }}>
        Your feedback history ({items.length})
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginBottom: 8 }}>
            Removing feedback takes it out of your history. A record that it existed is kept, the same way medical records keep corrections. {JOURNAL_PRIVACY}
          </div>
          {items.map((f) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0", fontSize: 12, color: C.muted }}>
              <span>
                <span style={{ color: C.text }}>{FEEDBACK_LABEL[f.feedback] || f.feedback}</span>
                {" · "}{(f.created_at || "").slice(0, 10)}{" · "}{f.shown_text}
              </span>
              <Btn C={C} onClick={() => onWithdraw(f)}>Remove</Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReflectionCard({ C, r, plan, versions, onPlan, onFeedback, onMute, given, canSave, busy }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(plan || "");
  useEffect(() => { setDraft(plan || ""); }, [plan]);
  const dirty = draft !== (plan || "");

  return (
    <article style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, background: C.surf2 }}>
      <p style={{ margin: "0 0 10px", fontSize: 14, lineHeight: 1.6, color: C.text }}>{r.text}</p>

      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ background: "none", border: "none", padding: "4px 0", color: C.teal, fontSize: 12, cursor: "pointer" }}>
        {open ? "Hide" : "Show"} the {r.count} entries behind this
      </button>
      {open && (
        <ul style={{ margin: "6px 0 0 18px", padding: 0, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
          {r.evidence.map((ev) => (
            <li key={ev.id}>{ev.date || "Undated entry"}{ev.pain ? ` · pain ${ev.pain}/10` : ""}</li>
          ))}
        </ul>
      )}

      {r.recordedActions.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
          In those crises you recorded using:{" "}
          <span style={{ color: C.text }}>
            {r.recordedActions.map((a) => `${a.name} (${a.count})`).join(", ")}
          </span>
        </div>
      )}

      <label style={{ display: "block", marginTop: 12, fontSize: 12, color: C.muted }}>
        Your plan: when I notice this, I usually…
        <textarea
          value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={500}
          style={{
            display: "block", width: "100%", marginTop: 6, padding: 10, borderRadius: 8, boxSizing: "border-box",
            background: C.surf, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: "inherit",
          }}
        />
      </label>
      {dirty && (
        <div style={{ marginTop: 6 }}>
          <Btn C={C} active onClick={() => onPlan(draft)}>{canSave ? "Save my plan" : "Keep for this visit"}</Btn>
        </div>
      )}
      <PlanVersions C={C} versions={versions} onUse={setDraft} />

      <div role="group" aria-label="How well does this fit you?" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
        {FEEDBACK_OPTIONS.map((o) => (
          <Btn key={o.id} C={C} active={given === o.id} color={o.id === "harmful" ? C.red : undefined}
            disabled={busy} onClick={() => onFeedback(o.id)}>{o.label}</Btn>
        ))}
        <Btn C={C} onClick={onMute} label="Mute this reflection">Mute</Btn>
      </div>
    </article>
  );
}

export default function SignalJournal({ entries, authSession, api, C }) {
  const token = authSession?.accessToken || null;
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [given, setGiven] = useState({});
  const [status, setStatus] = useState(null);
  const [showMuted, setShowMuted] = useState(false);
  const [showFactors, setShowFactors] = useState(false);
  const [history, setHistory] = useState([]);
  const [versions, setVersions] = useState({});

  useEffect(() => {
    if (!token) { setSettings(EMPTY_SETTINGS); setHistory([]); setVersions({}); return; }
    loadSettings(api, token).then(setSettings).catch(() => setStatus("Your journal settings couldn't load. Changes will only last for this visit."));
    loadFeedback(api, token).then(setHistory).catch(() => setHistory([]));
    loadPlanVersions(api, token).then((rows) => setVersions(groupVersions(rows))).catch(() => setVersions({}));
  }, [token, api]);

  const result = useMemo(() => buildReflections(entries, {
    excludedFactors: settings.excluded_factors, mutedKeys: settings.muted_keys,
  }), [entries, settings.excluded_factors, settings.muted_keys]);
  const factors = useMemo(() => availableFactors(entries), [entries]);

  function update(next) {
    setSettings(next);
    if (!token) return Promise.resolve(false);
    return saveSettings(api, token, next)
      .then(() => { setStatus(null); return true; })
      .catch(() => { setStatus("That change couldn't be saved. It will last for this visit only."); return false; });
  }

  const mute = (key) => update({ ...settings, muted_keys: [...new Set([...settings.muted_keys, key])] });
  const unmute = (key) => update({ ...settings, muted_keys: settings.muted_keys.filter((k) => k !== key) });
  // Saving a plan adds a version to its history (server-side trigger when
  // signed in; kept locally for the demo view).
  const setPlan = (key, text) => {
    update({ ...settings, plans: { ...settings.plans, [key]: text } }).then((saved) => {
      if (saved) {
        loadPlanVersions(api, token).then((rows) => setVersions(groupVersions(rows))).catch(() => {});
      } else if (!token) {
        const v = { id: `local-${Date.now()}`, plan_key: key, plan_text: text, created_at: new Date().toISOString() };
        setVersions((all) => ({ ...all, [key]: [v, ...(all[key] || [])] }));
      }
    });
  };
  const toggleFactor = (f) => update({
    ...settings,
    excluded_factors: settings.excluded_factors.includes(f)
      ? settings.excluded_factors.filter((x) => x !== f)
      : [...settings.excluded_factors, f],
  });

  const [sending, setSending] = useState(null);

  // "Not me" and "Harmful" hide the reflection for this Warrior (governance
  // packet §6). Signed in, nothing changes on screen until the server confirms
  // the feedback and the mute together; on failure neither is shown as saved.
  function feedback(r, id) {
    const hides = id === "not_me" || id === "harmful";
    const done = id === "harmful" ? "Thank you. That reflection is hidden." : null;
    if (!token) {
      const local = { id: `local-${Date.now()}`, reflection_key: r.key, feedback: id, shown_text: r.text, created_at: new Date().toISOString() };
      setGiven((g) => ({ ...g, [r.key]: id }));
      setHistory((h) => [local, ...h]);
      if (hides) setSettings((st) => ({ ...st, muted_keys: [...new Set([...st.muted_keys, r.key])] }));
      setStatus(done ? `${done} Demo view: nothing is saved.` : null);
      return;
    }
    if (sending) return;
    setSending(r.key);
    recordFeedback(api, token, r, id)
      .then((row) => {
        setGiven((g) => ({ ...g, [r.key]: id }));
        setHistory((h) => [row, ...h.filter((x) => x.id !== row.id)]);
        if (Array.isArray(row.muted_keys)) setSettings((st) => ({ ...st, muted_keys: row.muted_keys }));
        setStatus(done);
      })
      .catch(() => setStatus("Your feedback couldn't be saved, and nothing was changed. Please try again."))
      .finally(() => setSending(null));
  }

  function withdraw(item) {
    const drop = () => setHistory((h) => h.filter((x) => x.id !== item.id));
    if (!token || String(item.id).startsWith("local-")) { drop(); return; }
    withdrawFeedback(api, token, item.id)
      .then(drop)
      .catch(() => setStatus("That feedback couldn't be removed. Please try again."));
  }

  const visible = result.reflections.filter((r) => !r.muted);
  const muted = result.reflections.filter((r) => r.muted);

  return (
    <div className="fade" style={{
      background: C.surf, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 20,
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 13, color: C.amber, letterSpacing: 0.5 }}>SIGNAL JOURNAL</div>
        <div style={{ fontSize: 12, color: C.muted }}>Based only on the entries you submitted</div>
      </div>

      <UrgentPanel C={C} />

      {status && <div role="status" style={{ fontSize: 12, color: C.amber, marginBottom: 12 }}>{status}</div>}

      {result.status === "silent" && (
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
          Not enough entries yet. Your journal starts reflecting once you have logged at least two crises.
        </p>
      )}
      {result.status === "no_repeats" && (
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
          Nothing has repeated across your {result.total} entries yet, so there is nothing to reflect back. That is information too.
        </p>
      )}

      {visible.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visible.map((r) => (
            <ReflectionCard key={r.key} C={C} r={r} given={given[r.key]} canSave={!!token}
              plan={settings.plans[r.key]} versions={versions[r.key]} onPlan={(t) => setPlan(r.key, t)}
              onFeedback={(id) => feedback(r, id)} onMute={() => mute(r.key)} busy={sending === r.key} />
          ))}
        </div>
      )}
      {result.status === "ok" && visible.length === 0 && (
        <p style={{ fontSize: 13, color: C.muted }}>You have muted every reflection.</p>
      )}

      {muted.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={() => setShowMuted(!showMuted)} aria-expanded={showMuted}
            style={{ background: "none", border: "none", padding: "4px 0", color: C.muted, fontSize: 12, cursor: "pointer" }}>
            {muted.length} muted {muted.length === 1 ? "reflection" : "reflections"} · {showMuted ? "hide" : "show"}
          </button>
          {showMuted && muted.map((r) => (
            <div key={r.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12, color: C.muted, padding: "6px 0" }}>
              <span>{r.text}</span>
              <Btn C={C} onClick={() => unmute(r.key)}>Unmute</Btn>
            </div>
          ))}
        </div>
      )}

      {factors.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <button type="button" onClick={() => setShowFactors(!showFactors)} aria-expanded={showFactors}
            style={{ background: "none", border: "none", padding: "4px 0", color: C.teal, fontSize: 12, cursor: "pointer" }}>
            Choose which factors your journal uses
          </button>
          {showFactors && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {factors.map((f) => (
                <Btn key={f} C={C} active={!settings.excluded_factors.includes(f)} onClick={() => toggleFactor(f)}>{f}</Btn>
              ))}
            </div>
          )}
        </div>
      )}

      <FeedbackHistory C={C} items={history} onWithdraw={withdraw} />

      <label style={{ display: "block", marginTop: 14, fontSize: 12, color: C.muted }}>
        My general plan (optional)
        <GeneralPlan C={C} value={settings.plans[GENERAL_PLAN] || ""} canSave={!!token} versions={versions[GENERAL_PLAN]}
          onSave={(t) => setPlan(GENERAL_PLAN, t)} />
      </label>

      <div style={{ marginTop: 14, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
        {CRISIS_ONLY_NOTE} Use your own plan, notice changes, and decide when to seek care. Urgent symptoms always go to the separate path above.
        <div style={{ marginTop: 6, opacity: 0.8 }}>
          Rule-based reflection ({RULE_VERSION}), no AI model. {JOURNAL_PRIVACY}
          {!token && " Demo view: changes are not saved."}
        </div>
      </div>
    </div>
  );
}

function GeneralPlan({ C, value, onSave, canSave, versions }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={1000}
        placeholder="What helps you, in your own words"
        style={{
          display: "block", width: "100%", marginTop: 6, padding: 10, borderRadius: 8, boxSizing: "border-box",
          background: C.surf2, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: "inherit",
        }} />
      {draft !== value && (
        <div style={{ marginTop: 6 }}>
          <Btn C={C} active onClick={() => onSave(draft)}>{canSave ? "Save my plan" : "Keep for this visit"}</Btn>
        </div>
      )}
      <PlanVersions C={C} versions={versions} onUse={setDraft} />
    </>
  );
}
